import {
  Alert,
  AloftArea,
  AloftDate,
  AloftPrice,
  Improvements,
  ManualAdjustmentKey,
  ORDER_FEATURES,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftArea,
  schAloftPrice,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import { CompAdjustments } from '../../../../redux/features/selectors/CompAdjustments.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import {
  COMP_PRESENCE_ADJUSTMENT_CHECKS,
  CompPresenceAdjustmentCheck,
} from '../utils/compPresenceAdjustmentChecks.ts';
import { getComparableDisplayName, getScopedAlertType } from '../utils/compTypeScoping.ts';
import { getSubjectDataForCompType } from '../utils/getSubjectDataForCompType.ts';
import {
  BathCounts,
  hasBasementDifference,
  hasBathDifference,
  hasGarageCarportDifference,
  hasGlaDifference,
  hasRatingDifference,
  hasSaleDateDifference,
  hasSiteSizeDifference,
} from '../utils/unexpectedAdjustmentDifference.ts';

type SubjectData = SubjectPropertyDataState['subjectData']['editableData'];
type CompData = CompPropertyDataState['compData']['editableData'];

const TARGET_CELL_TOOLTIP = 'Unexpected adjustment';

// Adjustment magnitude above which an adjustment with no difference is flagged. Rating-level features
// and Date of Sale flag any non-zero adjustment; quantitative features flag above $10k.
const ANY_ADJUSTMENT_DOLLAR_THRESHOLD = AloftPrice.zero;
const LARGE_ADJUSTMENT_DOLLAR_THRESHOLD = AloftPrice.fromDollarLike(10000);

/**
 * Quantitative / qualitative fields
 *
 * Fires when an adjustment exceeds the feature's dollar threshold yet the Subject and Comp do not
 * differ for that feature. The difference thresholds live in unexpectedAdjustmentDifference.ts and
 * are intentionally independent of the missingCompAdjustment materiality bands.
 */
type DifferenceCheck = {
  label: string;
  cellKey: ManualAdjustmentKey;
  dollarThreshold: AloftPrice;
  getSubjectValue: (subject: SubjectData, effectiveDate: AloftDate) => unknown;
  getCompValue: (comp: CompData) => unknown;
  getAdjustment: (compAdjustments: CompAdjustments) => AloftPrice | undefined;
  hasDifference: (params: { subjectValue: unknown; compValue: unknown }) => boolean;
};

const DIFFERENCE_CHECKS: DifferenceCheck[] = [
  {
    label: 'Date of Sale',
    cellKey: 'dateOfSale',
    dollarThreshold: ANY_ADJUSTMENT_DOLLAR_THRESHOLD,
    getSubjectValue: (_s, effectiveDate) => effectiveDate,
    getCompValue: (c) => c.transaction?.saleDate,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.dateOfSale?.adjustment),
    hasDifference: ({ subjectValue, compValue }) =>
      hasSaleDateDifference(subjectValue as AloftDate, compValue as string),
  },
  {
    label: 'Site Size',
    cellKey: 'site',
    dollarThreshold: LARGE_ADJUSTMENT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => orUndefined(schAloftArea.parse)(s.site?.area),
    getCompValue: (c) => orUndefined(schAloftArea.parse)(c.site?.area),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.site?.size?.adjustment),
    hasDifference: ({ subjectValue, compValue }) =>
      hasSiteSizeDifference(subjectValue as AloftArea, compValue as AloftArea),
  },
  {
    label: 'Site Influence',
    cellKey: 'siteInfluence',
    dollarThreshold: ANY_ADJUSTMENT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.site?.influenceRating,
    getCompValue: (c) => c.site?.influenceRating,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.siteInfluence?.adjustment),
    hasDifference: ({ subjectValue, compValue }) => hasRatingDifference(subjectValue, compValue),
  },
  {
    label: 'View',
    cellKey: 'siteView',
    dollarThreshold: ANY_ADJUSTMENT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.site?.viewRating,
    getCompValue: (c) => c.site?.viewRating,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.siteView?.adjustment),
    hasDifference: ({ subjectValue, compValue }) => hasRatingDifference(subjectValue, compValue),
  },
  {
    label: 'Quality',
    cellKey: 'quality',
    dollarThreshold: ANY_ADJUSTMENT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements?.uadQuality,
    getCompValue: (c) => c.improvements?.uadQuality,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.quality?.adjustment),
    hasDifference: ({ subjectValue, compValue }) => hasRatingDifference(subjectValue, compValue),
  },
  {
    label: 'Condition',
    cellKey: 'condition',
    dollarThreshold: ANY_ADJUSTMENT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements?.uadCondition,
    getCompValue: (c) => c.improvements?.uadCondition,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.condition?.adjustment),
    hasDifference: ({ subjectValue, compValue }) => hasRatingDifference(subjectValue, compValue),
  },
  {
    label: 'GLA',
    cellKey: 'gla',
    dollarThreshold: LARGE_ADJUSTMENT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => orUndefined(schAloftArea.parse)(s.improvements?.gla),
    getCompValue: (c) => orUndefined(schAloftArea.parse)(c.improvements?.gla),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.mainDwelling?.gla?.adjustment),
    hasDifference: ({ subjectValue, compValue }) =>
      hasGlaDifference(subjectValue as AloftArea, compValue as AloftArea),
  },
  {
    label: 'Baths',
    cellKey: 'bathAboveGrade',
    dollarThreshold: LARGE_ADJUSTMENT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements,
    getCompValue: (c) => c.improvements,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.bathAboveGrade?.adjustment),
    hasDifference: ({ subjectValue, compValue }) =>
      hasBathDifference(subjectValue as BathCounts, compValue as BathCounts),
  },
  {
    label: 'Garage/Carport',
    cellKey: 'garageCarport',
    dollarThreshold: LARGE_ADJUSTMENT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements,
    getCompValue: (c) => c.improvements,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.garageCarport?.adjustment),
    hasDifference: ({ subjectValue, compValue }) =>
      hasGarageCarportDifference(subjectValue as Improvements, compValue as Improvements),
  },
  {
    label: 'Basement',
    cellKey: 'basement',
    dollarThreshold: LARGE_ADJUSTMENT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements,
    getCompValue: (c) => c.improvements,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.basement?.adjustment),
    hasDifference: ({ subjectValue, compValue }) =>
      hasBasementDifference(subjectValue as Improvements, compValue as Improvements),
  },
  {
    label: 'Basement Baths',
    cellKey: 'bathBelowGrade',
    dollarThreshold: LARGE_ADJUSTMENT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements?.basement,
    getCompValue: (c) => c.improvements?.basement,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.bathBelowGrade?.adjustment),
    hasDifference: ({ subjectValue, compValue }) =>
      hasBathDifference(subjectValue as BathCounts, compValue as BathCounts),
  },
];

/**
 * Binary presence / absence fields
 *
 * Fires when a feature is present on BOTH properties (or absent on both), yet a non-zero
 * adjustment is recorded. This is the inverse of the missingCompAdjustment presence check.
 * Manual Cost is unexpected-only and stays local.
 */
const PRESENCE_CHECKS: CompPresenceAdjustmentCheck[] = [
  ...COMP_PRESENCE_ADJUSTMENT_CHECKS,
  {
    label: 'Manual Cost',
    cellKey: 'manual',
    subjectHasFeature: (s) => ORDER_FEATURES.manualCost.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.manualCost.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.manual?.adjustment),
  },
];

export const unexpectedCompAdjustment: AlertDefinition = ({
  compAdjustments,
  compSequence,
  fullCompSequence,
  compType,
  effectiveDate,
  order,
  propertyDataEntities,
}) => {
  const subjectData = (propertyDataEntities.subject as SubjectPropertyDataState | undefined)
    ?.subjectData.editableData;

  if (!subjectData) return [];

  const scopedSubjectData = getSubjectDataForCompType(subjectData, compType);

  const fixRoute =
    schPairedCostRouteItem.enum[order.isInspectifyOrder ? 'adjustments-grid' : 'adjustments'];
  const triggers = getAlertTriggers({ fixRoute, order });

  return compSequence.flatMap((id) => {
    const compData = (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData
      .editableData;
    const compAdjustmentData = compAdjustments[id];
    if (!compData || !compAdjustmentData) return [];

    const propertyName = getComparableDisplayName(id, fullCompSequence ?? compSequence);

    const baseAlert = (cellKey: ManualAdjustmentKey, message: string, tips: string): Alert => ({
      type: getScopedAlertType(schAlertType.enum.unexpectedCompAdjustment, compType),
      status: 'active' as const,
      severity: 'error' as const,
      orderType: ['saas', 'appraisify'] as const,
      message,
      propertyID: id,
      ...(compType ? { compType } : {}),
      fixingInfo: {
        tips,
        pairedCostRouteItem: fixRoute,
        fixingMethod: ['comment', 'action'] as const,
        commentAlwaysRequired: false,
        targetCellKey: `${id}-${cellKey}` as PropertyGridCellKey,
        targetCellTooltip: TARGET_CELL_TOOLTIP,
      },
      triggers,
    });

    const differenceAlerts = DIFFERENCE_CHECKS.flatMap((check) => {
      const subjectValue = check.getSubjectValue(scopedSubjectData, effectiveDate);
      const compValue = check.getCompValue(compData);
      if (!isDefined(subjectValue) || !isDefined(compValue)) return [];

      const adjustment = check.getAdjustment(compAdjustmentData);
      if (!isDefined(adjustment) || !adjustment.abs().gt(check.dollarThreshold)) return [];

      if (check.hasDifference({ subjectValue, compValue })) return [];

      const { label, cellKey } = check;
      return [
        baseAlert(
          cellKey,
          `${propertyName} has a ${label} adjustment.`,
          'Since both properties have similar feature, an adjustment is typically not required. Review the entry or explain the factors contributing to this difference.',
        ),
      ];
    });

    const presenceAlerts = PRESENCE_CHECKS.flatMap((check) => {
      const subjectHas = check.subjectHasFeature(scopedSubjectData);
      const compHas = check.compHasFeature(compData);
      if (subjectHas !== compHas) return [];

      const adjustment = check.getAdjustment(compAdjustmentData);
      if (!isDefined(adjustment) || adjustment.equals(AloftPrice.zero)) return [];

      const { label, cellKey } = check;
      const bothVerb = subjectHas ? 'have' : 'do not have';
      return [
        baseAlert(
          cellKey,
          `${propertyName} has a ${label} adjustment, but both the Subject and ${propertyName} ${bothVerb} a ${label}.`,
          `Since both the Subject and ${propertyName} ${bothVerb} a ${label}, no adjustment is expected for this feature. Remove the adjustment, or explain why it is justified.`,
        ),
      ];
    });

    return [...differenceAlerts, ...presenceAlerts];
  });
};
